import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

// Neutralize CSV/Excel formula injection: cells starting with = + - @ (or tab/CR) become text.
function safeCell(v) {
  if (v == null) return "";
  const s = typeof v === "string" ? v : String(v);
  return /^[=+\-@\t\r]/.test(s) ? `'${s}` : s;
}

export function exportExcel(filename, columns, rows) {
  const data = rows.map((r) => {
    const o = {};
    columns.forEach((c) => { o[c.label] = safeCell(r[c.key]); });
    return o;
  });
  const ws = XLSX.utils.json_to_sheet(data);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Report");
  XLSX.writeFile(wb, `${filename}.xlsx`);
}

export function exportCSV(filename, columns, rows) {
  const esc = (v) => {
    const s = safeCell(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const header = columns.map((c) => esc(c.label)).join(",");
  const body = rows.map((r) => columns.map((c) => esc(r[c.key])).join(",")).join("\n");
  const blob = new Blob([`${header}\n${body}`], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${filename}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// Print a report table via a hidden iframe (clean, branded, printer-friendly).
export function printReport(title, columns, rows, subtitle = "") {
  const esc = (v) => String(v ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
  const thead = columns.map((c) => `<th style="text-align:${c.money ? "right" : "left"}">${esc(c.label)}</th>`).join("");
  const tbody = rows.map((r) => `<tr>${columns.map((c) => `<td style="text-align:${c.money ? "right" : "left"}">${esc(r[c.key])}</td>`).join("")}</tr>`).join("");
  const html = `<!doctype html><html><head><title>${esc(title)}</title>
    <style>
      body{font-family:-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif;color:#111;margin:32px}
      h1{color:#2563eb;font-size:18px;margin:0 0 2px}
      h2{font-size:13px;font-weight:600;margin:0 0 2px;color:#333}
      p.sub{color:#666;font-size:11px;margin:0 0 16px}
      table{width:100%;border-collapse:collapse;font-size:11px}
      th,td{padding:6px 8px;border-bottom:1px solid #e5e7eb}
      th{background:#2563eb;color:#fff;text-transform:uppercase;font-size:9px;letter-spacing:.04em}
      tbody tr:nth-child(even){background:#f5f8fc}
      @media print{body{margin:12px}}
    </style></head><body>
    <h1>Kora Holding, LLC</h1>
    <h2>${esc(title)}</h2>
    <p class="sub">${subtitle ? esc(subtitle) + " · " : ""}Generated ${esc(new Date().toLocaleString())}</p>
    <table><thead><tr>${thead}</tr></thead><tbody>${tbody || `<tr><td colspan="${columns.length}" style="text-align:center;color:#888;padding:24px">No data</td></tr>`}</tbody></table>
    </body></html>`;
  const iframe = document.createElement("iframe");
  iframe.style.position = "fixed";
  iframe.style.right = "0";
  iframe.style.bottom = "0";
  iframe.style.width = "0";
  iframe.style.height = "0";
  iframe.style.border = "0";
  document.body.appendChild(iframe);
  const doc = iframe.contentWindow.document;
  doc.open();
  doc.write(html);
  doc.close();
  iframe.contentWindow.focus();
  setTimeout(() => {
    iframe.contentWindow.print();
    setTimeout(() => document.body.removeChild(iframe), 1000);
  }, 300);
}

export function exportPDF(title, columns, rows) {
  const doc = new jsPDF({ orientation: columns.length > 6 ? "landscape" : "portrait" });
  doc.setFontSize(16);
  doc.setTextColor(37, 99, 235);
  doc.text("Kora Holding, LLC", 14, 16);
  doc.setFontSize(11);
  doc.setTextColor(60);
  doc.text(title, 14, 24);
  doc.setFontSize(8);
  doc.setTextColor(120);
  doc.text(`Generated ${new Date().toLocaleString()}`, 14, 29);
  autoTable(doc, {
    startY: 34,
    head: [columns.map((c) => c.label)],
    body: rows.map((r) => columns.map((c) => safeCell(r[c.key]))),
    headStyles: { fillColor: [37, 99, 235] },
    styles: { fontSize: 8 },
    alternateRowStyles: { fillColor: [245, 248, 252] },
  });
  doc.save(`${title.replace(/\s+/g, "-").toLowerCase()}.pdf`);
}
